import React, { useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Heart, Star, ChevronLeft, Truck, ShieldCheck, RotateCcw, BarChart2, Facebook, Twitter, Link2, Play } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import ProductCard, { formatPrice } from "../components/ProductCard";
import { priceInfo } from "../mock";

const WhatsAppIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-.607zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
  </svg>
);
const PinterestIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 12.017.026z"/>
  </svg>
);

const ytId = (url) => { const m = url && url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/); return m ? m[1] : null; };

function Stars({ value, size = 16 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={size} className={i <= Math.round(value) ? "fill-current" : ""} style={{ color: i <= Math.round(value) ? "var(--brand)" : "#d4d4d4" }} />
      ))}
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, tr, lang, products, categories, settings, wishlist, toggleWishlist, compare, toggleCompare, trackWhatsapp, getBadges } = useApp();
  const product = products.find((p) => p.id === id);
  const [active, setActive] = useState(0);
  const [showVideo, setShowVideo] = useState(false);

  const related = useMemo(
    () => products.filter((p) => p.category === product?.category && p.id !== product?.id).slice(0, 4),
    [products, product]
  );

  if (!product) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 py-32 text-center">
        <p className="text-neutral-500 mb-6">{t("noProducts")}</p>
        <Link to="/shop" className="text-sm tracking-widest uppercase" style={{ color: "var(--brand)" }}>{t("shop")}</Link>
      </div>
    );
  }

  const name = lang === "en" && product.nameEn ? product.nameEn : product.name;
  const inWish = wishlist.includes(product.id);
  const inComp = compare.includes(product.id);
  const badges = getBadges();
  const pi = priceInfo(product);
  const cleanNum = (settings.whatsappNumber || "").replace(/[^0-9]/g, "");
  const shareUrl = window.location.href;
  const pImg = (product.images && product.images[0]) || "";
  const imgAbs = pImg.startsWith("http") ? pImg : (pImg && !pImg.startsWith("data:") ? `${window.location.origin}${pImg}` : "");
  const priceLine = pi.hasDiscount
    ? `${formatPrice(pi.final)} (${t("waListPrice")}: ${formatPrice(pi.original)})`
    : formatPrice(pi.final);
  const waLines = [
    t("waOrderIntro"),
    "",
    `🛍️ ${t("waProduct")}: ${name}`,
    product.sku ? `🔖 ${t("waStock")}: ${product.sku}` : "",
    `💰 ${t("waPrice")}: ${priceLine}`,
    imgAbs ? `🖼️ ${t("waImage")}: ${imgAbs}` : "",
    `🔗 ${t("waLink")}: ${shareUrl}`,
  ].filter(Boolean);
  const msg = encodeURIComponent(waLines.join("\n"));
  const waPhone = `https://wa.me/${cleanNum}?text=${msg}`;
  const reviews = product.reviews || [];
  const vid = ytId(product.video);
  const catObj = categories.find((c) => c.slug === product.category);
  const subObj = catObj?.subcategories.find((s) => s.slug === product.subcategory);
  const catText = [tr(catObj?.name), tr(subObj?.name)].filter(Boolean).join(" › ");

  const specs = product.specs || {};
  const specRows = [
    ["specBrand", specs.brand],
    ["specMovement", tr(specs.movement)],
    ["specCase", tr(specs.caseMaterial)],
    ["specDiameter", specs.diameter],
    ["specGlass", tr(specs.glass)],
    ["specWater", specs.waterResistance],
    ["specWarranty", tr(specs.warranty)],
  ].filter(([, v]) => v);

  const handleCompare = () => {
    if (!inComp && compare.length >= 4) return toast.error(t("maxCompare"));
    toggleCompare(product.id);
    if (!inComp) toast.success(t("addedCompare"));
  };
  const copyLink = async () => { try { await navigator.clipboard.writeText(shareUrl); toast.success(t("linkCopied")); } catch { toast.error("!"); } };
  const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const tw = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(name)}`;
  const pin = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&media=${encodeURIComponent(product.images[0])}&description=${encodeURIComponent(name)}`;

  return (
    <div>
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-xs tracking-widest uppercase text-neutral-500 hover:text-[color:var(--brand)] transition-colors">
          <ChevronLeft size={16} /> {lang === "tr" ? "Geri" : "Back"}
        </button>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 pb-16 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
        {/* Gallery */}
        <div className="flex flex-col-reverse md:flex-row gap-4">
          <div className="flex md:flex-col gap-3 overflow-x-auto">
            {product.images.map((img, i) => (
              <button key={i} onClick={() => { setActive(i); setShowVideo(false); }} className={`shrink-0 w-16 h-20 md:w-20 md:h-24 overflow-hidden border transition-colors ${active === i && !showVideo ? "border-[color:var(--brand)]" : "border-transparent"}`}>
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
            {(vid || product.videoFile) && (
              <button onClick={() => setShowVideo(true)} className={`relative shrink-0 w-16 h-20 md:w-20 md:h-24 overflow-hidden border transition-colors ${showVideo ? "border-[color:var(--brand)]" : "border-transparent"}`}>
                {vid ? (
                  <img src={`https://img.youtube.com/vi/${vid}/mqdefault.jpg`} alt="video" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-neutral-800" />
                )}
                <span className="absolute inset-0 flex items-center justify-center bg-black/40"><Play size={18} className="text-white fill-white" /></span>
              </button>
            )}
          </div>
          <div className="flex-1 relative aspect-[3/4] bg-neutral-100 overflow-hidden">
            {pi.hasDiscount && !showVideo && (
              <span className="absolute top-4 left-4 z-10 text-[10px] tracking-widest uppercase text-white px-3 py-1.5 font-semibold" style={{ backgroundColor: "#c1121f" }}>%{pi.pct} {t("discountOff")}</span>
            )}
            {showVideo && (vid || product.videoFile) ? (
              product.videoFile ? (
                <video src={product.videoFile} controls autoPlay className="w-full h-full object-contain bg-black" />
              ) : (
                <iframe title="video" className="w-full h-full" src={`https://www.youtube.com/embed/${vid}?autoplay=1`} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              )
            ) : (
              <img src={product.images[active]} alt={name} className="w-full h-full object-cover" />
            )}
          </div>
        </div>

        {/* Info */}
        <div className="lg:pt-4">
          <div className="flex flex-wrap gap-2 mb-4">
            {(product.badges || []).map((b) => (
              <span key={b} className="text-[9px] tracking-widest uppercase text-white px-2.5 py-1 font-medium" style={{ backgroundColor: badges[b]?.color }}>{tr(badges[b]) || b}</span>
            ))}
          </div>
          <h1 className="brand-font text-3xl md:text-4xl tracking-wide text-neutral-900 mb-4">{name}</h1>
          <div className="flex items-center gap-3 mb-6">
            <Stars value={product.rating} />
            <span className="text-xs text-neutral-500">{product.rating.toFixed(1)} · {reviews.length} {t("reviewCount")}</span>
          </div>

          {/* Price */}
          <div className="flex items-center gap-3 mb-8">
            {pi.hasDiscount ? (
              <>
                <span className="text-2xl line-through font-light" style={{ color: "#c1121f" }}>{formatPrice(pi.original)}</span>
                <span className="text-3xl text-neutral-900 font-medium">{formatPrice(pi.final)}</span>
              </>
            ) : (
              <span className="text-3xl text-neutral-900 font-light">{formatPrice(pi.final)}</span>
            )}
          </div>

          <p className="text-neutral-600 leading-relaxed font-light mb-8">{tr(product.description)}</p>
          <p className="text-xs tracking-wide text-neutral-500 mb-4 bg-neutral-50 border border-neutral-100 px-4 py-3">{t("orderNote")}</p>

          {/* WhatsApp buttons */}
          <div className="space-y-3">
            <a href={waPhone} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsapp(product.id)}
               className="flex items-center justify-center gap-3 w-full py-4 text-white text-sm tracking-widest uppercase transition-colors" style={{ backgroundColor: "#25D366" }}>
              <WhatsAppIcon size={20} /> {t("orderWhatsapp")}
            </a>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => toggleWishlist(product.id)} className="flex items-center justify-center gap-2 py-4 border border-neutral-300 text-xs tracking-widest uppercase text-neutral-700 hover:border-neutral-900 transition-colors">
                <Heart size={16} className={inWish ? "fill-current" : ""} style={{ color: inWish ? "var(--brand)" : undefined }} /> {inWish ? t("removeFromWishlist") : t("addToWishlist")}
              </button>
              <button onClick={handleCompare} className="flex items-center justify-center gap-2 py-4 border text-xs tracking-widest uppercase transition-colors" style={{ borderColor: inComp ? "var(--brand)" : "#d4d4d4", color: inComp ? "var(--brand)" : "#404040" }}>
                <BarChart2 size={16} /> {inComp ? t("removeFromCompare") : t("compare")}
              </button>
            </div>
          </div>

          {/* Share */}
          <div className="flex items-center gap-3 mt-6">
            <span className="text-xs tracking-widest uppercase text-neutral-400">{t("share")}:</span>
            <a href={fb} target="_blank" rel="noopener noreferrer" className="w-9 h-9 border border-neutral-200 flex items-center justify-center text-neutral-600 hover:text-white hover:bg-[#1877F2] hover:border-[#1877F2] transition-colors" title="Facebook"><Facebook size={16} /></a>
            <a href={tw} target="_blank" rel="noopener noreferrer" className="w-9 h-9 border border-neutral-200 flex items-center justify-center text-neutral-600 hover:text-white hover:bg-black hover:border-black transition-colors" title="X"><Twitter size={16} /></a>
            <a href={pin} target="_blank" rel="noopener noreferrer" className="w-9 h-9 border border-neutral-200 flex items-center justify-center text-neutral-600 hover:text-white hover:bg-[#E60023] hover:border-[#E60023] transition-colors" title="Pinterest"><PinterestIcon size={16} /></a>
            <button onClick={copyLink} className="w-9 h-9 border border-neutral-200 flex items-center justify-center text-neutral-600 hover:text-white hover:bg-neutral-800 hover:border-neutral-800 transition-colors" title={t("copyLink")}><Link2 size={16} /></button>
          </div>

          {/* Meta: stock code, categories, tags */}
          <div className="mt-6 pt-6 border-t border-neutral-100 space-y-2 text-sm">
            {product.sku && (
              <div className="flex gap-2"><span className="text-neutral-400 tracking-wide min-w-[110px]">{t("stockCode")}:</span><span className="text-neutral-700">{product.sku}</span></div>
            )}
            {catText && (
              <div className="flex gap-2"><span className="text-neutral-400 tracking-wide min-w-[110px]">{t("categoriesLabel")}:</span>
                <Link to={`/shop/${product.category}`} className="text-neutral-700 hover:text-[color:var(--brand)] transition-colors">{catText}</Link>
              </div>
            )}
            {(product.tags || []).length > 0 && (
              <div className="flex gap-2"><span className="text-neutral-400 tracking-wide min-w-[110px]">{t("tagsLabel")}:</span>
                <span className="flex flex-wrap gap-1.5">
                  {product.tags.map((tg, i) => (
                    <Link key={i} to={`/shop?q=${encodeURIComponent(tg)}`} className="text-xs bg-neutral-100 hover:bg-neutral-200 text-neutral-600 px-2.5 py-1 transition-colors">{tg}</Link>
                  ))}
                </span>
              </div>
            )}
          </div>

          {/* Trust row */}
          <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t border-neutral-100">
            {[[Truck, t("freeShipping")], [ShieldCheck, t("authentic")], [RotateCcw, t("support")]].map(([Ic, lb], i) => (
              <div key={i} className="flex flex-col items-center text-center gap-2">
                <Ic size={22} strokeWidth={1.2} style={{ color: "var(--brand)" }} />
                <span className="text-[10px] tracking-wide uppercase text-neutral-500">{lb}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Specs */}
      {specRows.length > 0 && (
        <div className="max-w-[900px] mx-auto px-4 md:px-8 pb-16">
          <h2 className="brand-font text-2xl tracking-wide mb-6">{t("specs")}</h2>
          <div className="border border-neutral-200">
            {specRows.map(([k, v], i) => (
              <div key={k} className={`grid grid-cols-2 ${i % 2 ? "bg-white" : "bg-neutral-50"}`}>
                <div className="px-5 py-3 text-xs tracking-widest uppercase text-neutral-500 border-r border-neutral-200">{t(k)}</div>
                <div className="px-5 py-3 text-sm text-neutral-800">{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reviews */}
      {reviews.length > 0 && (
        <div className="border-t border-neutral-200 bg-neutral-50">
          <div className="max-w-[1000px] mx-auto px-4 md:px-8 py-16">
            <h2 className="brand-font text-3xl tracking-wide text-center mb-10">{t("reviews")}</h2>
            <div className="space-y-6">
              {reviews.map((r) => (
                <div key={r.id} className="bg-white border border-neutral-100 p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-neutral-800">{r.name}</span>
                    <Stars value={r.rating} size={13} />
                  </div>
                  <p className="text-sm text-neutral-600 font-light leading-relaxed">{r.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Related */}
      {related.length > 0 && (
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16">
          <h2 className="brand-font text-3xl tracking-wide text-center mb-10">{t("relatedProducts")}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 md:gap-x-6">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      )}
    </div>
  );
}
